<script setup lang="ts">
import { updateCase } from '@/backend/updateCase'
import CaseMessageInput from '@/modules/Cases/CaseMessageInput.vue'
import { useUser } from '@/modules/IdentityAndAccess/useUser'
import WorkspaceFixedToolbar from '@/modules/Workspaces/WorkspaceFixedToolbar.vue'
import WorkspaceHomepageGreeting from '@/modules/Workspaces/WorkspaceHomepageGreeting.vue'
import { toast } from '@/shared/toast'
import { invariant } from '@/shared/utils/typeAssertions'
import { useDashboardFilterStore } from '@/sharedComposables/useDashboardFilterStore'
import { useDropZone, watchDebounced } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, useTemplateRef } from 'vue'
import CasesList from './CasesList.vue'
import NoCaseSearchResults from './NoCaseSearchResults.vue'
import { useCaseStore } from './useCaseStore'
import { useNewCase } from './useNewCase'

const props = defineProps<{ workspaceId: string }>()

const casesStore = useCaseStore()
const { hasMoreCases } = storeToRefs(casesStore)
const dashboardFilterStore = useDashboardFilterStore()
const {
  addIntegrationFiles,
  addLibraryFiles,
  addPendingUploads,
  agentId,
  attachments,
  statusText,
  messageText,
  removeUpload,
  startNewCase,
  webSearch,
  isSendable,
} = useNewCase()
const userStore = useUser()
const messageInputRef = useTemplateRef<HTMLDivElement>('messageInputRef')
const { isOverDropZone } = useDropZone(messageInputRef, {
  onDrop: (files) => {
    if (files && files.length > 0) {
      addPendingUploads(files)
    }
  },
})

const containerRef = useTemplateRef<HTMLDivElement>('containerRef')

type OrderField = 'id' | 'name' | 'updatedAt'
type OrderDirection = 'asc' | 'desc'
type OrderTuple = [OrderField, OrderDirection]

const searchParams = computed(() => {
  // Cases ID's are incremental, so we can use them to sort by creation date
  const sortValue =
    dashboardFilterStore.caseSortValue === 'createdAt' ? 'id' : dashboardFilterStore.caseSortValue

  const order = [[sortValue, dashboardFilterStore.caseSortDirection] as OrderTuple]

  return {
    nameContains: dashboardFilterStore.searchValue,
    workspaceId: props.workspaceId,
    ownerIds: dashboardFilterStore.caseOwnerIds,
    order,
  }
})

// Add watcher for search params changes
watchDebounced(
  () => [
    dashboardFilterStore.searchValue,
    dashboardFilterStore.caseSortValue,
    dashboardFilterStore.caseSortDirection,
    dashboardFilterStore.caseOwnerIds,
  ],
  async () => {
    casesStore.$reset()
    await casesStore.loadCases(searchParams.value)
  },
  { debounce: 300 },
)

const shouldShowNoCases = computed(() => !casesStore.isLoading && casesStore.cases.length === 0)

const shouldShowIsLoading = computed(() => casesStore.isLoading && casesStore.cases.length === 0)

const onRenameCase = async (id: string, name: string) => {
  const targetCase = casesStore.cases.find((c) => c.id === id)
  if (!targetCase) {
    invariant(targetCase, 'Case not found')
    toast.error('Case not found')
    return
  }

  const revert = casesStore.setCase({ ...targetCase, name })

  try {
    const res = await updateCase({ workspaceId: props.workspaceId, caseId: id, name })
    if (!res.ok) {
      revert()
      toast.error('Failed to rename case')
    }
  } catch {
    revert()
    toast.error('Failed to rename case')
  }
}

const scrollToTop = async () => {
  if (!containerRef.value) {
    return
  }

  await nextTick()

  // Only scroll if container has scrollable content
  const isScrollable = containerRef.value.scrollHeight > containerRef.value.clientHeight
  if (!isScrollable) {
    return
  }

  containerRef.value?.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

onMounted(() => {
  // Reset store when coming from case detail, with only the
  // active case loaded, to maintain correct sort order
  if (casesStore.cases.length === 1) {
    casesStore.$reset()
    return
  } else if (casesStore.cases.length > 1) {
    return
  }

  casesStore.loadCases(searchParams.value)
})
</script>

<template>
  <div
    ref="containerRef"
    class="go-scrollbar flex h-auto min-h-full flex-col gap-10 overflow-auto pb-4 [contain:strict]"
    data-test="cases-container"
  >
    <div class="mt-16 flex items-center justify-between">
      <WorkspaceHomepageGreeting :user="userStore.user" />
    </div>

    <CaseMessageInput
      ref="messageInputRef"
      v-model:text="messageText"
      v-model:selected-agent-id="agentId"
      v-model:web-search="webSearch"
      class="go-column px-2"
      :attachments="attachments"
      :placeholder="'Ask Go...'"
      :disabled="!isSendable"
      :is-dropping-files="isOverDropZone"
      @submit="
        startNewCase({
          workspaceId: props.workspaceId,
          initialMessage: messageText,
          navigate: 'push',
        })
      "
      @delete-attachment="removeUpload($event)"
      @import:library="addLibraryFiles($event)"
      @import:computer="addPendingUploads($event)"
      @import:google="addIntegrationFiles"
    >
      <template #status>
        <span
          v-if="statusText"
          class="ellipsis-loading flex flex-row items-center justify-start text-sm-12px-light text-text-subtlest"
        >
          {{ statusText }}
        </span>
      </template>
    </CaseMessageInput>

    <div class="flex grow flex-col items-start justify-start gap-4 pt-2">
      <WorkspaceFixedToolbar @scroll-to-top="scrollToTop" />
      <NoCaseSearchResults
        v-if="shouldShowNoCases"
        class="go-column px-2"
        @reset-filters="dashboardFilterStore.clear"
      />
      <div
        v-else
        class="go-column px-2"
      >
        <CasesList
          :loading="shouldShowIsLoading"
          :workspace-id="workspaceId"
          :cases="casesStore.cases"
          :has-more-cases="hasMoreCases"
          @rename="onRenameCase"
          @load-more="casesStore.loadCases(searchParams)"
        />
      </div>
    </div>
  </div>
</template>
